import {
  configureStore,
  combineReducers,
  createListenerMiddleware,
} from '@reduxjs/toolkit';
import organizationReducer, {
  OrganizationState,
  setOrganization,
} from './Slices/organizationSlice';
import globalReducer from './Slices/globalSlice';
import accessReducer from './Slices/accessSlice';
import teamReducer from './Slices/teamSlice';
import subscriptionReducer from './Slices/subscriptionSlice';
import productsReducer from './Slices/productsSlice';
import creditBalanceReducer from './Slices/creditBalance';
import { OrgCredential } from '../interfaces/organization';
import ActionError from '../utils/ActionError';
import { BeErrorCodeUnusableOrgCredential } from '../enums/beErrorCode';
import { trackException } from '../utils/monitoring';
import appLoadingReducer from './Slices/appLoadingSlice';
import appFeatureReducer from './Slices/appFeatureSlice';

// Import agent-specific reducers
import changeRequestItemsReducer from './Slices/ChangeRequestItemsSlice';
import changeRequestReducer from './Slices/changeRequestSlice';
import changeRequestStepsReducer from './Slices/changeRequestStepsSlice';
import changeRequestEditReducer from './Slices/changeRequestEditSlice';
import changeRequestStateReducer from './Slices/changeRequestStateSlice';
import adminAgentReducer from './Slices/adminAgentSlice';
import promptLibraryReducer from './Slices/promptLibrarySlice';

const listenerMiddlewareInstance = createListenerMiddleware({
  onError: (...args) => {
    trackException(new Error('Unhandle error detected:' + args));
  },
});
listenerMiddlewareInstance.startListening({
  predicate: (action) => action.type.endsWith('/rejected'),
  effect: (action, listenerApi) => {
    if ((action.payload as Record<string, string>)?.name !== ActionError.name) {
      return;
    }
    const error = action.payload as ActionError;
    const state = listenerApi.getState() as { organization: OrganizationState };
    const currentOrg = state?.organization?.org;
    if (!currentOrg?.active) {
      return;
    }

    const isSfExpiredError =
      error.errorCode &&
      (Object.values(BeErrorCodeUnusableOrgCredential) as string[]).includes(
        error.errorCode
      );
    if (!isSfExpiredError) {
      return;
    }
    const newOrg = { ...currentOrg, active: false } as OrgCredential;
    listenerApi.dispatch(setOrganization(newOrg));
  },
});

// Create agent reducer that combines all agent-specific slices
const agentReducer = combineReducers({
  // Change Request related state
  changeRequestItems: changeRequestItemsReducer,
  changeRequest: changeRequestReducer,
  changeRequestSteps: changeRequestStepsReducer,
  changeRequestEdit: changeRequestEditReducer,
  changeRequestState: changeRequestStateReducer,

  // Admin Agent related state
  adminAgent: adminAgentReducer,

  // Prompt Library state
  promptLibrary: promptLibraryReducer,
});

export const rootReducer = combineReducers({
  // Global app slices
  global: globalReducer,
  access: accessReducer,
  organization: organizationReducer, // Salesforce organizations
  team: teamReducer, // Cirra teams
  subscription: subscriptionReducer,
  appLoading: appLoadingReducer,
  appFeature: appFeatureReducer,
  products: productsReducer,
  creditBalance: creditBalanceReducer,

  // Agent-specific slices under agent namespace
  agent: agentReducer,
});

const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMw) =>
    getDefaultMw({
      // Disable ImmutableStateInvariantMiddleware in test environment
      // to prevent warnings after tests complete
      immutableCheck: process.env.NODE_ENV !== 'test',
      serializableCheck: process.env.NODE_ENV !== 'test',
    }).prepend(listenerMiddlewareInstance.middleware),
});

// RootState type (for state structure)
export type RootState = ReturnType<typeof store.getState>;

// AppDispatch type (for dispatch with thunks)
export type AppDispatch = typeof store.dispatch;

export default store;
